import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useOrganisation } from "../contexts/OrganisationContext";
import { cn } from "../lib/cn";

interface StandardEntry {
  moduleCode: string;
  shortLabel: string;
  fullLabel: string;
  standardCode: string;
  versionYear: number;
}

const STANDARDS: StandardEntry[] = [
  {
    moduleCode: "ISO9001_2015",
    shortLabel: "ISO 9001",
    fullLabel: "ISO 9001:2015 — Quality",
    standardCode: "ISO9001",
    versionYear: 2015,
  },
  {
    moduleCode: "ISO14001_2015",
    shortLabel: "ISO 14001",
    fullLabel: "ISO 14001:2015 — Environment",
    standardCode: "ISO14001",
    versionYear: 2015,
  },
  {
    moduleCode: "ISO27001_2022",
    shortLabel: "ISO 27001",
    fullLabel: "ISO 27001:2022 — Information security",
    standardCode: "ISO27001",
    versionYear: 2022,
  },
  {
    moduleCode: "ISO45001_2018",
    shortLabel: "ISO 45001",
    fullLabel: "ISO 45001:2018 — Occupational health and safety",
    standardCode: "ISO45001",
    versionYear: 2018,
  },
];

interface SidebarProps {
  orgSlug: string;
  /** Whether the mobile drawer is currently open. Ignored at >=md. */
  isOpen?: boolean;
  /** Called when the user dismisses the mobile drawer (overlay click, Esc, route change). */
  onClose?: () => void;
}

export function Sidebar({ orgSlug, isOpen = false, onClose }: SidebarProps) {
  const { isModuleActive } = useOrganisation();
  const { activeOrganisation } = useAuth();
  const location = useLocation();
  const role = activeOrganisation?.role ?? "Contributor";
  const isAdmin = role === "Admin";
  const isAuditor = role === "Auditor_Viewer";

  // Close on route change (mobile only — desktop sidebar ignores isOpen).
  useEffect(() => {
    if (isOpen && onClose) onClose();
  }, [location.pathname, isOpen, onClose]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Mobile backdrop. Only rendered when drawer is open. */}
      {isOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
        />
      ) : null}
      <aside
        className={cn(
          "z-50 w-60 shrink-0 border-r border-slate-200 bg-slate-50 transition-transform dark:border-slate-700 dark:bg-slate-950",
          // Mobile: fixed drawer, slides in from left.
          "fixed inset-y-0 left-0 -translate-x-full md:static md:translate-x-0",
          isOpen && "translate-x-0",
        )}
      >
        <nav aria-label="Primary" className="flex h-full flex-col gap-4 p-3 text-sm">
          <SidebarSection title="Workspace">
            <SidebarItem to={`/o/${orgSlug}/dashboard`} label="Dashboard" />
            <SidebarItem to={`/o/${orgSlug}/vault`} label="The Vault" />
            <SidebarItem to={`/o/${orgSlug}/standards`} label="Standards" />
            {!isAuditor ? (
              <SidebarItem to={`/o/${orgSlug}/nonconformities`} label="Nonconformities" />
            ) : null}
            <SidebarItem to={`/o/${orgSlug}/audit-log`} label="Audit log" />
          </SidebarSection>

          <SidebarSection title="ISO Standards">
            {STANDARDS.map((standard) => {
              const active = isModuleActive(standard.moduleCode);
              return active ? (
                <SidebarItem
                  key={standard.moduleCode}
                  to={`/o/${orgSlug}/standards/${standard.standardCode}/${standard.versionYear}`}
                  label={standard.shortLabel}
                  title={standard.fullLabel}
                />
              ) : (
                <SidebarItemLocked
                  key={standard.moduleCode}
                  to={`/o/${orgSlug}/billing/upgrade?moduleCode=${standard.moduleCode}`}
                  label={standard.shortLabel}
                  tooltip={`${standard.fullLabel} — click to subscribe.`}
                />
              );
            })}
          </SidebarSection>

          <SidebarSection title="Stakeholders">
            <SidebarItem to={`/o/${orgSlug}/parties`} label="Interested parties" />
            <SidebarItem to={`/o/${orgSlug}/network`} label="Supplier connections" />
            <SidebarItem to={`/o/${orgSlug}/network/supplier-evidence`} label="Supplier evidence" />
          </SidebarSection>

          <SidebarSection title="Settings">
            <SidebarItem to={`/o/${orgSlug}/settings`} label="Settings home" end />
            {isAdmin ? (
              <>
                <SidebarItem to={`/o/${orgSlug}/settings/members`} label="Members" />
                <SidebarItem to={`/o/${orgSlug}/settings/branding`} label="Branding" />
                <SidebarItem to={`/o/${orgSlug}/settings/billing-tier`} label="Company size" />
                <SidebarItem to={`/o/${orgSlug}/billing`} label="Billing" />
              </>
            ) : null}
          </SidebarSection>

          <div className="mt-auto pt-4">
            <Link
              to="/switch"
              className="block rounded px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-200/70 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              Switch organisation →
            </Link>
          </div>
        </nav>
      </aside>
    </>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h2>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

function SidebarItem({
  to,
  label,
  title,
  end,
}: {
  to: string;
  label: string;
  title?: string;
  end?: boolean;
}) {
  return (
    <li>
      <NavLink
        to={to}
        title={title}
        end={end}
        className={({ isActive }) =>
          cn(
            "block rounded px-2 py-1.5 text-slate-700 hover:bg-slate-200/70 dark:text-slate-200 dark:hover:bg-slate-800",
            isActive &&
              "bg-brand-50 font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-100",
          )
        }
      >
        {label}
      </NavLink>
    </li>
  );
}

function SidebarItemLocked({ to, label, tooltip }: { to: string; label: string; tooltip: string }) {
  return (
    <li>
      <Link
        to={to}
        title={tooltip}
        className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-slate-400 hover:bg-slate-200/50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <span>{label}</span>
        <LockIcon />
      </Link>
    </li>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="9" rx="1" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
